import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback } from 'react'
import { HOST_PROGRAMS_ANCHOR } from 'src/constants/hostPrograms'
import useSupplyLead from 'src/hooks/useSupplyLead'
import { RedirectUrls } from 'src/types/types'
import ga from 'src/utils/pixels/gtag'

/**
 * Slim host band above the FAQ — the first host-facing block after the hero.
 *
 * Why it exists (2026-09-06): the full-width host section that used to live
 * here drew 3–5 host clicks a week in July; once it was gone the only host
 * paths on `/` were the nav pill, the hero microlink and the footer card, and
 * the footer card drew ONE click from `/` in 30 days because it sits below
 * the FAQ and the footer link columns. About one in eighteen home sessions
 * goes on to a host page, and supply is the constraint. Kept slim and low on
 * the page so it does not pull renters out of the funnel — the reason the
 * old section was removed.
 *
 * Measurement: `anfitrion_click` location=`home_host_band` (same family as
 * `nav_desktop` / `nav_mobile` / `footer_banner`, so the elements are
 * comparable) plus the supply-side pixel via `useSupplyLead`, mirroring the
 * hero's microlink. Never `generateLead` — that is the demand-side family.
 * Insight `OAyfbl8t` on dashboard 1584362.
 */
const HostBand = () => {
  const { t } = useTranslation('landing')
  const { supplyLead } = useSupplyLead()

  const handlePublish = useCallback(() => {
    supplyLead({ funnel: 'individual', source: 'home_host_band' })
    ga.event('anfitrion_click', { location: 'home_host_band' })
  }, [supplyLead])

  // Second path for the host who is NOT an individual owner — operators,
  // companies, brokers, 3PLs — straight to the program selector on
  // /anfitrion. Same event family, own `location`, so the insight shows how
  // many hosts on `/` self-identify as non-individual.
  const handlePrograms = useCallback(() => {
    ga.event('anfitrion_click', { location: 'home_host_band_programs' })
  }, [])

  return (
    <section className="w-full border-t border-ink-100/15 bg-cream-300 px-5 py-12 md:px-10 md:py-16">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-7 md:flex-row md:items-center md:justify-between md:gap-12">
        {/* Copy names every host type SpotMe has a program for — owners,
            self-storage and parking operators, warehouses, brokers, 3PLs —
            because "Para propietarios" (the old section's eyebrow) read as
            owners-only (Jules, 2026-09-06). Keep the list in sync with
            PROGRAMS in anfitrion.json. */}
        <div className="max-w-2xl">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('INDEX_HOST_BAND.EYEBROW')}
          </p>
          <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[36px]">
            {t('INDEX_HOST_BAND.HEADING')}
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
            {t('INDEX_HOST_BAND.SUBLINE')}
          </p>
        </div>

        {/* Stacked on mobile, right-aligned column on desktop. The primary
            pill matches the hero's storage CTA chrome; the programs link is
            a quiet text link so the band reads as one ask, not two. */}
        <div className="flex shrink-0 flex-col gap-3 md:items-end">
          <Link
            href={RedirectUrls.Anfitrion}
            onClick={handlePublish}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-7 py-4 text-center text-base font-semibold text-white-500 transition-[background-color,transform] duration-200 hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 active:scale-[0.98] md:w-auto md:px-8"
          >
            <span>{t('INDEX_HOST_BAND.BUTTON')}</span>
            <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
          <Link
            href={`${RedirectUrls.Anfitrion}#${HOST_PROGRAMS_ANCHOR}`}
            onClick={handlePrograms}
            className="inline-flex min-h-11 items-center justify-center text-sm text-ink-700 underline decoration-ink-100/40 underline-offset-4 transition-colors hover:text-blue-500 hover:decoration-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 md:justify-end md:text-right"
          >
            {t('INDEX_HOST_BAND.SECONDARY')}
          </Link>
        </div>
      </div>
    </section>
  )
}

export default React.memo(HostBand)
